import { useState, useEffect } from "react"
import { pushDataLayer, getProductResponse } from "@/lib/helpers/tagManager"
import Step from "./step"
import useCheckoutContext, {
  useStepByName,
  useToken,
  useTrackingItem,
  useFeeAmount,
  useMembershipAmount
} from "./store"
import { useCartContext } from "@/components/woocommerce/cartStore"
import { useRouter } from "next/router"
import { addOrderLog } from "@/lib/api/checkoutForm"
import { fetchFormData } from "@/lib/api/telemarketing"
import { Spinner } from "@/common/loading/spinner"
import { parse } from "next-useragent"
import InApp from "detect-inapp"
import Label from "@/components/common/form/label"
import styles from "@/components/checkout-form/payment/gateways/bankIdStyles.module.css"

function Steps({ form, tracking }) {
  const { setToken } = useToken()
  const { setTrackingItem } = useTrackingItem()

  const { steps, resetContext, activeStepIndex } = useCheckoutContext()
  const { query } = useRouter()
  const [defaultValues, setDefaultValues] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const [showForm, setShowForm] = useState(true)
  const [orderSessionId] = useState(
    Date.now().toString(36) +
      Math.floor(
        Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)
      ).toString(36)
  )
  const { setFeeAmount } = useFeeAmount()
  const { setMembershipAmount } = useMembershipAmount()

  useEffect(() => {
    if (query.token !== undefined) {
      setShowForm(false)
      loadData(form.type, query.token, form)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  useEffect(() => {
    setFeeAmount(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check if the token is valid
  const loadData = async (form, token, formSettings) => {
    const response = await fetchFormData(form, token)
    if (response.fetchFormData.success) {
      response.fetchFormData.data.amount = parseInt(
        response.fetchFormData.data.amount
      )

      if (formSettings.membershipAmounts !== undefined) {
        switch (response.fetchFormData.data.membertype) {
          case "familyMembership":
            setMembershipAmount(formSettings.membershipAmounts.family_member)
            break
          default:
            setMembershipAmount(formSettings.membershipAmounts.member)
            break
        }
      }

      setDefaultValues(response.fetchFormData.data)
      setShowForm(true)
      setToken(token)
    } else {
      setFetchError(true)
    }
  }

  const isGiftShop = useStepByName("giftShopCheckout") ?? false
  const { data } = useCartContext()
  const { items } = data || {}

  let products = [{ ...tracking }]
  const { type } = form

  useEffect(() => {
    if (type === "monthlyDonorTM") {
      setShowForm(false)
      setTimeout(function (showForm) {
        if (!showForm) {
          setFetchError(true)
        }
      }, 4000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  let formType = type

  if (isGiftShop) {
    formType = "giftShop"
    products = getProductResponse(items)
  }

  useEffect(() => {
    if (!tracking && !isGiftShop) {
      return
    }

    const prevStep = steps[activeStepIndex - 1] ?? {}
    const { name } = prevStep

    setTrackingItem(products)

    if (activeStepIndex === 0) {
      let ua = parse(window.navigator.userAgent)
      const inApp = new InApp(window.navigator.userAgent)
      let isInApp = inApp.isInApp
      const browser = ua.browser.toLowerCase()
      const os = ua.os.toLowerCase()

      if (browser === "chrome" && os === "android") {
        isInApp = false
      }

      addOrderLog(orderSessionId, "Session startad", {
        agent: window.navigator.userAgent,
        form: type,
        inApp: isInApp
      })

      if (window?.Cookiebot?.consent?.statistics) {
        addOrderLog(orderSessionId, "Cookie - statistics", "true")
      } else {
        window.addEventListener(
          "CookiebotOnAccept",
          function () {
            if (window?.Cookiebot?.consent?.statistics) {
              addOrderLog(orderSessionId, "Cookie - statistics", "true")
            }
          },
          false
        )
      }

      pushDataLayer(
        {
          event: "begin_checkout",
          items: products
        },
        true
      )
    }

    if (activeStepIndex <= 0) {
      return
    }

    pushDataLayer(
      {
        event: "form",
        step: activeStepIndex,
        stepName: name,
        formType,
        ecommerce: {
          checkout: {
            actionField: {
              step: activeStepIndex
            },
            items: products
          }
        }
      },
      true
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex])

  useEffect(() => {
    resetContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  if (showForm) {
    return (
      <>
        <FormInfo texts={form.extraInfo} />
        <AllSteps
          steps={steps}
          defaultValues={defaultValues}
          formType={formType}
          orderSessionId={orderSessionId}
          formSettings={form}
        />
      </>
    )
  } else {
    if (!fetchError) {
      return (
        <div className="flex justify-center pt-24">
          <Spinner className="flex justify-center ml-2" />
        </div>
      )
    } else {
      return (
        <div className="flex justify-center pt-24">
          Länken är inte längre giltig.
        </div>
      )
    }
  }
}

const AllSteps = ({
  steps,
  defaultValues,
  formType,
  orderSessionId,
  formSettings
}) => {
  return steps.map((step, index) => (
    <Step
      key={index}
      index={index}
      defaultValues={defaultValues}
      type={formType}
      orderSessionId={orderSessionId}
      formSettings={formSettings}
    />
  ))
}

const FormInfo = ({ texts }) => {
  if (!texts) {
    return null
  }
  return (
    <div className="px-6 py-4 mb-6 rounded-sm bg-summer-green-50">
      <div
        className={styles.contentHolder}
        dangerouslySetInnerHTML={{ __html: texts.content }}></div>

      {texts.list.length > 0 &&
        texts.list.map((item, index) => (
          <Label
            key={index}
            tooltip={item.help_text}
            className="flex items-center text-sm">
            {item.text}
          </Label>
        ))}
    </div>
  )
}

export default Steps
